import React, { useCallback, useState } from 'react';
import { useIsRequiredElement } from '../../../../hook/useIsRequiredElement';
import ElementErrorMessage from '../components/element-error-message';
import SettingsModal from './components/settingsModal';
import { useIsInitFormPage } from '../../../../hook/pages/useIsInitFormPage';
import cx from 'classnames';
import { getLayoutOptionsClass } from '../../../from-builder/elements-settings/components/layout-options-setting';
import { GridYValue, GridYUnit } from '../../../../constants';

export const CheckboxElement = function FromElement({
  element,
  handlerChange,
  error,
  duplicateElement,
  removeElement,
  isPrint,
  formHasOnlyOneSubmitter,
  strictOrderStep,
}) {
  const isRequiredElement = useIsRequiredElement(element, formHasOnlyOneSubmitter, undefined, strictOrderStep);
  const isInitFormPage = useIsInitFormPage();
  const { options, id: elementId } = element;
  const { initiator_can_edit, alignment } = options ?? {};
  const currentUserCanEdit = isInitFormPage && initiator_can_edit;

  // The group label is optional and usually left empty - a single "I confirm
  // ..." checkbox carries its text on the option itself. The label line is
  // still drawn (it is part of the height every saved form was laid out
  // against), but with nothing in it the required mark was left floating above
  // the checkbox, over whatever the field was drawn on top of. With no label
  // text of its own the mark rides the last option instead, where the reader
  // is looking.
  const hasGroupLabel = !!String(options.label ?? '').trim();
  const markRidesOption = isRequiredElement && !hasGroupLabel && element.inputs.length > 0;

  const [isOpenSettingsModal, setIsOpenSettingsModal] = useState(false);

  const handelOpenSettingsModal = () => setIsOpenSettingsModal(true);

  const handelCloseSettingsModal = useCallback(() => setIsOpenSettingsModal(false), []);

  return (
    <div className={'element-wrapper checkbox'}>
      <div className={'element-inner'}>
        <label
          htmlFor={element.id}
          className="form-label w-full"
          style={{ zIndex: 5000 + (element.options.zIndex || 10), width: 'fit-content', position: 'relative' }}
        >
          {element.options.label} <span className="required">{isRequiredElement && !markRidesOption ? '*' : ''}</span>
        </label>
        {/* "Layout options" arranges the options the same way here as on the
            builder canvas and in the printed PDF. Defaults to vertical. */}
        <div className={cx('form-control-checkbox-wrapper', getLayoutOptionsClass(options))}>
          {element.inputs.map((input, index) => {
            const { id: inputId } = input;
            const isChecked = !!input.valueInput;
            const inputLabelId = `${elementId}-${inputId}`;
            return (
              <div
                key={index}
                className={cx('form-control-checkbox element', {
                  print: isPrint,
                  [alignment]: alignment,
                  checked: isChecked,
                  [`form-control-checkbox-${element.options.size}`]: element.options.size,
                })}
                style={{
                  zIndex: 5000 + (element.options.zIndex || 10),
                  position: 'relative',
                }}
              >
                <label
                  className={'checkbox-label'}
                  htmlFor={inputLabelId}
                  style={{
                    zIndex: 5000 + (element.options.zIndex || 10),
                    position: 'relative',
                    fontFamily: 'Arial',
                  }}
                >
                  {!isPrint && (
                    <input
                      id={inputLabelId}
                      name={element.options.label}
                      type="checkbox"
                      value={inputId}
                      checked={isChecked}
                      readOnly={input.readOnly}
                      onChange={(e) => handlerChange(element, e.target.checked, false, index)}
                    />
                  )}
                  <span
                    className={cx('checkbox-button', { checked: isChecked })}
                    style={{ zIndex: 5000 + (element.options.zIndex || 10), position: 'relative', minWidth: '20px' }}
                  />
                  {input.label}
                  {markRidesOption && index === element.inputs.length - 1 && <span className="required">*</span>}
                </label>
              </div>
            );
          })}
        </div>
      </div>

      <ElementErrorMessage message={error} />
      {element.options.instructions && <p className="instructions">{element.options.instructions}</p>}

      {currentUserCanEdit && (
        <div className={'init-form-aside-buttons'}>
          <button onClick={() => duplicateElement(element.id)} className="btn btn-settings btn-icon init-form-copy-element-btn">
            <i className="icon-copy" />
          </button>
          <button onClick={handelOpenSettingsModal} className="btn btn-settings btn-icon settings-modal-btn">
            <i className="icon-settings" />
          </button>
          <button onClick={() => removeElement(element.id)} className="btn btn-settings btn-icon btn-remove">
            <i className="icon-remove"></i>
          </button>
        </div>
      )}
      {currentUserCanEdit && (
        <SettingsModal element={element} isOpen={isOpenSettingsModal} handlerChange={handlerChange} onClose={handelCloseSettingsModal} />
      )}
    </div>
  );
};
